import React, { useState, createContext, useContext, useEffect } from 'react';

const ThemeContext = createContext(null);

function ThemeProvider({ children }) {
  const [themeMode, setThemeModeState] = useState(() => {
    return localStorage.getItem('themeMode') || 'system';
  });

  const [resolvedTheme, setResolvedTheme] = useState('light');

  const [visualEffect, setVisualEffectState] = useState(() => {
    return localStorage.getItem('visualEffect') || 'medium';
  });
  
  const [customEffectSettings, setCustomEffectSettingsState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('customEffectSettings')) || { blur: true, animation: true };
    } catch {
      return { blur: true, animation: true };
    }
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');

    const updateTheme = () => {
      let newTheme = 'light';
      if (themeMode === 'system') {
        newTheme = mediaQuery ? (mediaQuery.matches ? 'dark' : 'light') : 'light';
      } else {
        newTheme = themeMode;
      }
      setResolvedTheme(newTheme);
      document.documentElement.setAttribute('data-theme', newTheme);
    };

    updateTheme();

    if (mediaQuery) {
      const handler = () => updateTheme();
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [themeMode]);

  useEffect(() => {
    document.documentElement.setAttribute('data-visual-effect', visualEffect);
    
    let enableBlur = true;
    let enableAnim = true;
    let highBlur = false;
    
    if (visualEffect === 'lowest') {
       enableBlur = false;
       enableAnim = false;
    } else if (visualEffect === 'low') {
       enableBlur = true; 
       enableAnim = false; 
    } else if (visualEffect === 'medium') {
       enableBlur = true;
       enableAnim = true;
    } else if (visualEffect === 'high') {
       enableBlur = true;
       enableAnim = true;
       highBlur = true;
    } else if (visualEffect === 'custom') {
       enableBlur = customEffectSettings.blur;
       enableAnim = customEffectSettings.animation;
    }
    
    document.documentElement.setAttribute('data-effect-blur', enableBlur ? 'true' : 'false');
    document.documentElement.setAttribute('data-effect-anim', enableAnim ? 'true' : 'false');
    document.documentElement.setAttribute('data-effect-high-blur', highBlur ? 'true' : 'false');
  }, [visualEffect, customEffectSettings]);

  const setThemeMode = (mode) => {
    setThemeModeState(mode);
    localStorage.setItem('themeMode', mode);
  };

  const setVisualEffect = (effect) => {
    setVisualEffectState(effect);
    localStorage.setItem('visualEffect', effect);
  };

  const setCustomEffectSettings = (settings) => {
    setCustomEffectSettingsState(settings);
    localStorage.setItem('customEffectSettings', JSON.stringify(settings));
  };

  return (
    <ThemeContext.Provider value={{ 
      themeMode, setThemeMode, resolvedTheme,
      visualEffect, setVisualEffect, customEffectSettings, setCustomEffectSettings
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export { ThemeContext, ThemeProvider, useTheme };
